/**
 * What the thread header's assignee button should say and do.
 *
 * Extracted from ConversationHeader so the decision can be unit-tested
 * without booting a Svelte component runtime. All inputs are values (no
 * stores, no derivations); the component wraps this in `$derived` and feeds
 * `session.state.sso?.user?.id` from the live runes.
 *
 * This file used to be `lib/claim/format.ts` and also formatted a countdown.
 * Claims expired on a timer; assignments do not (2026-09-07). The countdown
 * went with the timer, and so did the two states that only made sense while
 * one existed — "refresh", and "blocked because their claim is still warm".
 */

import type { AssigneeRef } from '../api/queries.js';

export type AssigneeButtonMode = 'hidden' | 'assign' | 'release' | 'takeover';

export interface AssigneeButtonState {
  mode: AssigneeButtonMode;
  /** Label rendered on the button. Empty string when mode === 'hidden'. */
  label: string;
}

export interface AssigneeButtonStateInput {
  conversationId: string | null;
  currentUserId: string | null;
  assignee: AssigneeRef | null;
}

/**
 * Decide the button given the current operator and the row's assignee.
 *
 *   - no conversation OR pre-SSO   → hidden
 *   - held by me                   → release
 *   - held by someone else         → takeover (the server may answer 409)
 *   - unassigned                   → assign
 *
 * Takeover stays ENABLED even though the server refuses it while the other
 * operator still holds the row. An operator who cannot tell the difference
 * between "not allowed" and "button missing" will assume the product is
 * broken; a 409 with a name on it tells them who to go and ask.
 */
export function computeAssigneeButtonState(
  input: AssigneeButtonStateInput,
): AssigneeButtonState {
  const { conversationId, currentUserId, assignee } = input;
  if (!conversationId || !currentUserId) {
    return { mode: 'hidden', label: '' };
  }
  if (assignee !== null && assignee.userId === currentUserId) {
    return { mode: 'release', label: 'Release' };
  }
  if (assignee !== null) {
    return { mode: 'takeover', label: 'Take over' };
  }
  return { mode: 'assign', label: 'Assign to me' };
}
